import { GoogleOAuthProvider } from "@react-oauth/google";
import { persistor, store } from "~/store";
import React from "react";
import { ClearCacheProvider } from "react-clear-cache";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "./state/auth/AuthContext";
import { AppProviders } from './AppProviders';
import App from "./views/app/App";

import "./assets/styles/index.scss";
import "./i18n/index";

// react-clear-cache listens to the native `storage` event and calls JSON.parse
// on t.newValue for every localStorage change — without a try/catch. When any
// other tab stores a raw (non-JSON) value (e.g. a JWT token, UUID, "whatsapp"),
// the parse throws a SyntaxError. We intercept window.addEventListener before
// ClearCacheProvider mounts so every future `storage` listener is wrapped in a
// try/catch that silently swallows SyntaxErrors.
const _origAddEventListener = window.addEventListener.bind(window);
window.addEventListener = function (type, listener, ...args) {
  if (type === "storage") {
    return _origAddEventListener(type, function (event) {
      try { listener(event); } catch (e) {
        if (!(e instanceof SyntaxError)) throw e;
      }
    }, ...args);
  }
  return _origAddEventListener(type, listener, ...args);
};

// Also sanitize any existing APP_VERSION that may have been stored without
// JSON.stringify (a raw UUID would fail on the next polling tick).
try {
  const raw = localStorage.getItem("APP_VERSION");
  if (raw !== null) JSON.parse(raw);
} catch (_) {
  localStorage.removeItem("APP_VERSION");
}

if (typeof Node !== 'undefined') {
  const _origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return _origInsertBefore.call(this, newNode, referenceNode);
  };

  const _origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      return child;
    }
    return _origRemoveChild.call(this, child);
  };
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
  release: process.env.REACT_APP_VERSION,
  enabled: !!process.env.REACT_APP_SENTRY_DSN,
  maxBreadcrumbs: 150,
  tracesSampleRate: 0,
  defaultIntegrations: false,
  integrations: [
    new Sentry.Integrations.Breadcrumbs(),
    new Sentry.Integrations.GlobalHandlers({ onerror: true, onunhandledrejection: false }),
    new Sentry.Integrations.LinkedErrors(),
    new Sentry.Integrations.Dedupe(),
  ],
  ignoreErrors: ['Script error.'],
  beforeSend(event) {
    const isSyntaxError = event.exception?.values?.some(
      (ex) => ex.type === 'SyntaxError'
    );
    if (isSyntaxError) return null;

    const isScriptError = event.exception?.values?.some(
      (ex) => ex.value === 'Script error.'
    );
    if (isScriptError) return null;

    return event;
  },
});

window.addEventListener('unhandledrejection', (event) => {
  event.preventDefault();
  event.stopImmediatePropagation();
});

ReactDOM.render(
	<ClearCacheProvider duration={5000}>
		<Provider store={store}>
		<AuthProvider>
			<PersistGate loading={null} persistor={persistor}>
				<GoogleOAuthProvider>
					<AppProviders>
						<BrowserRouter>
							<Sentry.ErrorBoundary fallback={null} showDialog={false}>
								<App />
							</Sentry.ErrorBoundary>
						</BrowserRouter>
					</AppProviders>
				</GoogleOAuthProvider>
			</PersistGate>
		</AuthProvider>
		</Provider>
	</ClearCacheProvider>,
	document.getElementById("root")
);
